/**
 * 一个帮助操作 section 相关 api 的类
 * 设计上是一个 presentationAPI 的友元，但是没有想到太好的实现方式就单独作为一个类放了出来
 */
import { DataType } from '../../data';
import type { ISectionDataModel } from '../../model/section-data-model';
import type { ISlideDataModel } from '../../model/slide-data-model';

export class SectionMutationHelper {
    private sectionList: ISectionDataModel[]; // 节列表
    private slideList: ISlideDataModel[]; // 页列表
    private ignoreSlideList: string[] = []; // 忽略的页id

    private bindMutationMap = new Map<string, string | undefined>(); // section -> slide

    public constructor(
        slideList: ISlideDataModel[],
        sectionList: ISectionDataModel[],
        private bindSectionToSlide: (section: ISectionDataModel, firstSlideIndex: number) => void, // api 里 section 绑定 slide 的方法
    ) {
        this.slideList = slideList;
        this.sectionList = sectionList;
    }

    public updateSlideList(slideList: ISlideDataModel[]) {
        this.slideList = slideList;
    }

    public updateSectionList(sectionList: ISectionDataModel[]) {
        this.sectionList = sectionList;
    }

    /**
     * 根据 slide 找到目标节，进行改绑定操作
     * @param originBindSlideId
     * @param withoutFirstSection
     * @returns
     */
    public rebindBySlide(originBindSlideId: string | undefined) {
        for (let i = this.sectionList.length - 1; i >= 0; i -= 1) {
            const section = this.sectionList[i];
            if (section.props.firstSlideId !== originBindSlideId) {
                continue;
            }
            this.rebind(section);
            return;
        }
    }

    /**
     * 指定插入的 slide 和插入的 gapIndex 进行改绑定操作
     * 因为指定了 slide，因此不需要再向下找绑定的目标 slide 了，找到 section 后直接绑定即可
     * @param slideId
     * @param gapIndex
     * @returns
     */
    public rebindWhenInsertSlide(slideId: string, gapIndex: number) {
        for (let i = gapIndex; i >= 0; i -= 1) {
            if (this.sectionSlideList[i].type === DataType.Section) {
                this.bindMutationMap.set(this.sectionSlideList[i].id, slideId);
                continue;
            }
            if (this.ignoreSlideList.includes(this.sectionSlideList[i].id)) {
                continue;
            }
            return;
        }
    }

    /**
     * 设置需要忽略的 slides
     * @param slideList
     */
    public setIgnoreSlides(slideList: ISlideDataModel[]) {
        for (const ignoreSlide of slideList) {
            this.ignoreSlideList.push(ignoreSlide.id);
        }
    }

    public requestBindTask() {
        this.bindMutationMap.forEach((slideId, sectionId) => {
            const section = this.sectionList.find((section) => section.id === sectionId);
            if (!section) {
                return;
            }
            if (section.props.firstSlideId === slideId) {
                return;
            }
            const slideIndex = this.slideList.findIndex((slide) => slide.id === slideId);
            this.bindSectionToSlide(section, slideIndex >= 0 ? slideIndex : this.slideList.length);
        });
    }

    public requestRenameTask(renameList: string[]) {
        this.sectionList.forEach((section, index) => {
            if (section.props.name === renameList[index]) {
                return;
            }
            section.api.updateProps({ name: renameList[index] });
        });
    }

    /**
     * 主动修改 section 的绑定信息
     * @param section
     * @param slideId
     */
    public addBindMutation(section: ISectionDataModel, slideId: string | undefined) {
        this.bindMutationMap.set(section.id, slideId);
    }

    /**
     * passDown 操作：修改此节和此节上方的空节的绑定状态，绑定到节下的第一个 slide
     * @param targetSection 目标节
     */
    private rebind(targetSection: ISectionDataModel) {
        const originBindSlideId = targetSection.props.firstSlideId;
        // 找到此 section 和此 section 上方的空 section
        const passDownSections: ISectionDataModel[] = [];
        for (const section of this.sectionList) {
            if (section.props.firstSlideId === targetSection.props.firstSlideId) {
                passDownSections.push(section);
            }
            if (section.id === targetSection.id) {
                break;
            }
        }
        // 找到节下第一个 slide 并设置绑定
        const slideIndex = this.slideList.findIndex((slide) => slide.id === originBindSlideId);
        let bindSlideIndex = slideIndex;
        for (bindSlideIndex = slideIndex; bindSlideIndex < this.slideList.length; bindSlideIndex += 1) {
            if (this.ignoreSlideList.includes(this.slideList[bindSlideIndex]?.id)) {
                continue;
            }
            break;
        }
        const bindSlideId = this.slideList[bindSlideIndex]?.id;
        for (const section of passDownSections) {
            this.bindMutationMap.set(section.id, bindSlideId);
        }
    }

    /**
     * 平铺的 section-slide 列表
     */
    private get sectionSlideList(): (ISectionDataModel | ISlideDataModel)[] {
        const sectionSlideList: (ISectionDataModel | ISlideDataModel)[] = [];
        const { sectionList, slideList } = this;
        for (const slide of slideList) {
            // 获取绑定在此 slide 上的其他节
            for (const section of sectionList) {
                if (section.props.firstSlideId === slide.id) {
                    sectionSlideList.push(section);
                }
            }
            sectionSlideList.push(slide);
        }
        const lastSections = sectionList.filter((section) => section.props.firstSlideId === undefined);
        sectionSlideList.push(...lastSections);
        return sectionSlideList;
    }
}
